import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  ExternalLink,
  CheckCircle,
  Loader2,
  ChevronRight,
  Target,
  TrendingUp,
  FileText,
  Menu,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Smooth scroll to section
const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Sobre", id: "sobre" },
    { label: "Newsletter", id: "newsletter" },
    { label: "Terrenos", id: "terrenos" },
    { label: "Contacto", id: "contacto" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header header-scrolled" : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <button
            data-testid="logo"
            onClick={() => scrollToSection("hero")}
            className="font-display text-xl sm:text-2xl font-semibold text-foreground hover:text-primary transition-colors"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Ivo Gomes
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors link-underline"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              data-testid="header-cta-newsletter"
              onClick={() => scrollToSection("newsletter")}
              className="btn-primary text-sm"
            >
              Subscrever newsletter
            </Button>
            <Button
              data-testid="header-cta-terrenos"
              onClick={() => scrollToSection("terrenos")}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-sm font-medium px-6 py-3 rounded-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-95"
            >
              Vender terreno
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden py-4 border-t border-border animate-fade-in"
          >
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-${link.id}`}
                  onClick={() => {
                    scrollToSection(link.id);
                    setMobileMenuOpen(false);
                  }}
                  className="text-left py-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="flex flex-col gap-2 pt-4 border-t border-border mt-2">
                <Button
                  data-testid="mobile-cta-newsletter"
                  onClick={() => {
                    scrollToSection("newsletter");
                    setMobileMenuOpen(false);
                  }}
                  className="btn-primary w-full"
                >
                  Subscrever newsletter
                </Button>
                <Button
                  data-testid="mobile-cta-terrenos"
                  onClick={() => {
                    scrollToSection("terrenos");
                    setMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="btn-outline w-full"
                >
                  Vender terreno
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Newsletter Form Component
const NewsletterForm = ({ variant = "default", id }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!email || !email.includes("@")) {
      setError("Por favor, insira um email válido.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("https://api.resend.com/emails", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer re_3o5xjELB_LWGSbXR7iJ9W9SXAvA5zxNXF" },
        body: JSON.stringify({
          from: "website@ivopedrogomes.com",
          to: "ivo@maisonporto.com",
          subject: "Nova subscrição newsletter",
          html: `<p>Novo subscritor: <strong>${email}</strong></p>`
        })
      });
      setSuccess(true);
      setEmail("");
      toast.success("Subscrição efectuada com sucesso!");
    } catch (err) {
      const message = err.response?.data?.detail || "Erro ao subscrever. Tente novamente.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={`${id}-success`}
        className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-md animate-fade-in"
      >
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        <p className="text-green-800 text-sm">Subscrição efectuada com sucesso!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full" data-testid={id}>
      <div className={`flex ${variant === "inline" ? "flex-col sm:flex-row" : "flex-col"} gap-3`}>
        <div className="flex-1">
          <Input
            data-testid={`${id}-email-input`}
            type="email"
            placeholder="O seu email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`input-custom ${error ? "form-error" : ""}`}
            disabled={loading}
          />
          {error && (
            <p data-testid={`${id}-error`} className="text-destructive text-sm mt-1">
              {error}
            </p>
          )}
        </div>
        <Button
          data-testid={`${id}-submit`}
          type="submit"
          disabled={loading}
          className="btn-primary whitespace-nowrap"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              A subscrever...
            </>
          ) : (
            "Subscrever newsletter"
          )}
        </Button>
      </div>
    </form>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="min-h-screen flex items-center pt-20 sm:pt-24 pb-16 hero-bg"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="animate-slide-up">
            <h1
              data-testid="hero-title"
              className="heading-display text-foreground mb-6"
            >
              Investimento imobiliário no Porto/Norte: mercado e oportunidades
            </h1>
            <p
              data-testid="hero-subtitle"
              className="body-large mb-8 max-w-xl"
            >
              Newsletter mensal para investidores. Partilho leitura do mercado e 
              oportunidades de investimento com critérios claros e foco na execução.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mb-8">
              <div data-testid="hero-stat-1" className="text-center sm:text-left">
                <p className="text-3xl sm:text-4xl font-bold text-primary" style={{ fontFamily: "'Playfair Display', serif" }}>2013</p>
                <p className="text-sm text-muted-foreground">No mercado desde</p>
              </div>
              <div data-testid="hero-stat-2" className="text-center sm:text-left">
                <p className="text-3xl sm:text-4xl font-bold text-primary" style={{ fontFamily: "'Playfair Display', serif" }}>1000+</p>
                <p className="text-sm text-muted-foreground">Transações realizadas</p>
              </div>
            </div>

            {/* Newsletter Form */}
            <div className="mb-6">
              <NewsletterForm variant="inline" id="hero-newsletter-form" />
            </div>

            {/* Additional CTAs */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button
                data-testid="hero-cta-newsletter"
                onClick={() => scrollToSection("newsletter")}
                variant="outline"
                className="btn-outline"
              >
                Ver newsletter
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                data-testid="hero-cta-terrenos"
                onClick={() => scrollToSection("terrenos")}
                variant="ghost"
                className="text-muted-foreground hover:text-foreground"
              >
                Vender terreno urbano
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Trust Note */}
            <p data-testid="hero-trust-note" className="text-sm text-muted-foreground">
              1 email/mês. Sem spam. Cancelamento em 1 clique.
            </p>
          </div>

          {/* Image */}
          <div className="hidden lg:block animate-fade-in animation-delay-200">
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1765100780182-69667db54d95"
                alt="Porto - Investimento Imobiliário"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Para Quem Section
const ParaQuemSection = () => {
  const items = [
    {
      icon: Target,
      title: "Investidores que procuram oportunidades no Porto/Norte",
    },
    {
      icon: TrendingUp,
      title: "Investidores que valorizam sinais do mercado: liquidez, preço, risco e prazo",
    },
    {
      icon: FileText,
      title: "Investidores que preferem informação prática e orientada a decisão",
    },
  ];

  return (
    <section
      id="para-quem"
      data-testid="para-quem-section"
      className="section-spacing section-alt"
    >
      <div className="container-custom">
        <h2 data-testid="para-quem-title" className="heading-section text-center mb-16">
          Para quem é
        </h2>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {items.map((item, index) => (
            <div
              key={index}
              data-testid={`para-quem-item-${index}`}
              className="card-custom card-lift text-center"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-6">
                <item.icon className="w-6 h-6 text-primary" />
              </div>
              <p className="body-regular text-foreground">{item.title}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <p data-testid="para-quem-cta-text" className="body-large mb-6">
            Recebe oportunidades de investimento antes de estas chegarem ao mercado.
          </p>
          <Button
            data-testid="para-quem-cta-btn"
            onClick={() => scrollToSection("newsletter")}
            className="btn-primary"
          >
            Subscrever newsletter
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const benefits = [
    "Síntese mensal do mercado (Porto/Norte): sinais e tendências com leitura operacional",
    "Oportunidades seleccionadas (quando existirem), com racional e principais riscos",
    "Critérios de análise: localização, liquidez, margem, prazos e entraves",
    "Checklist curta: diligência prévia e negociação",
    "Ligações úteis: dados e leituras relevantes",
  ];

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="section-spacing"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <h2 data-testid="newsletter-title" className="heading-section mb-8">
              O que recebe na newsletter
            </h2>

            <ul className="space-y-4 mb-10">
              {benefits.map((benefit, index) => (
                <li
                  key={index}
                  data-testid={`newsletter-benefit-${index}`}
                  className="flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="body-regular text-muted-foreground">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Newsletter Form */}
            <div className="mb-6">
              <NewsletterForm id="newsletter-section-form" />
            </div>

            {/* Trust Block */}
            <p
              data-testid="newsletter-trust"
              className="text-sm text-muted-foreground border-l-2 border-primary/30 pl-4"
            >
              Informação baseada no terreno e em contacto com o mercado. 
              Não constitui aconselhamento financeiro.
            </p>
          </div>

          {/* Image - Porto Map */}
          <div className="hidden lg:block">
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1768501364341-0eaa6c52e14d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxQb3J0byUyMFBvcnR1Z2FsJTIwY2l0eSUyMG1hcCUyMGFlcmlhbHxlbnwwfHx8fDE3NzA1NzQyNjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Vista aérea do Porto - Ribeira e Rio Douro"
                className="w-full h-full object-cover"
                data-testid="newsletter-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sobre Section
const SobreSection = () => {
  return (
    <section
      id="sobre"
      data-testid="sobre-section"
      className="section-spacing section-alt"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="aspect-square max-w-md rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_4a9b03c2-47d5-44de-ab45-afbd53f86a06/artifacts/ix5d9bwy_28F82991-B65C-4A30-9357-B5A690C6EA64_1_105_c.jpeg"
                alt="Ivo Gomes"
                className="w-full h-full object-cover"
                data-testid="sobre-image"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <h2 data-testid="sobre-title" className="heading-section mb-6">
              Sobre
            </h2>

            <p data-testid="sobre-text" className="body-large mb-8">
              Sou Ivo Gomes, sócio e gestor na Maison Porto. Actuo no Porto/Norte, 
              focado em mediação e consultoria imobiliária com abordagem pragmática, 
              diligência prévia e transparência.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="https://www.linkedin.com/in/ivopedrogomes/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sobre-linkedin"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                <Linkedin className="w-5 h-5" />
                LinkedIn
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href="https://www.maisonporto.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sobre-maison"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                Maison Porto
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Terrenos Form Section
const TerrenosSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    localizacao: "",
    area_m2: "",
    situacao: "",
    mensagem: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome.trim()) newErrors.nome = "Nome é obrigatório";
    if (!formData.email.trim()) newErrors.email = "Email é obrigatório";
    else if (!formData.email.includes("@")) newErrors.email = "Email inválido";
    if (!formData.localizacao.trim()) newErrors.localizacao = "Localização é obrigatória";
    if (!formData.situacao) newErrors.situacao = "Situação é obrigatória";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await fetch("https://api.resend.com/emails", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer re_3o5xjELB_LWGSbXR7iJ9W9SXAvA5zxNXF" },
        body: JSON.stringify({
          from: "website@ivopedrogomes.com",
          to: "ivo@maisonporto.com",
          subject: "Novo lead - Terreno",
          html: `<p><strong>Nome:</strong> ${formData.nome}</p><p><strong>Email:</strong> ${formData.email}</p><p><strong>Telefone:</strong> ${formData.telefone}</p><p><strong>Localização:</strong> ${formData.localizacao}</p><p><strong>Área:</strong> ${formData.area}</p><p><strong>Situação:</strong> ${formData.situacao}</p><p><strong>Mensagem:</strong> ${formData.mensagem}</p>`
        })
      });
      setSuccess(true);
      toast.success("Pedido recebido com sucesso!");
    } catch (err) {
      const message = err.response?.data?.detail || "Erro ao enviar pedido. Tente novamente.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section
        id="terrenos"
        data-testid="terrenos-section"
        className="section-spacing"
      >
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div
              data-testid="terrenos-success"
              className="p-8 bg-green-50 border border-green-200 rounded-lg animate-fade-in"
            >
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-green-800 mb-2">
                Pedido recebido
              </h3>
              <p className="text-green-700">
                Respondo assim que possível.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="terrenos"
      data-testid="terrenos-section"
      className="section-spacing"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Form */}
          <div>
            <h2 data-testid="terrenos-title" className="heading-section mb-4">
              Tem um terreno urbano e quer vender a promotores?
            </h2>
            <p data-testid="terrenos-description" className="body-large mb-8">
              Se é proprietário de terreno urbano (ou imóvel com potencial de reconversão), 
              faço uma avaliação inicial e verifico interesse junto de promotores, 
              com abordagem discreta e orientada a fecho.
            </p>

            <form onSubmit={handleSubmit} data-testid="terrenos-form" className="space-y-5">
              {/* Nome */}
              <div>
                <Label htmlFor="nome" className="text-sm font-medium mb-2 block">
                  Nome *
                </Label>
                <Input
                  id="nome"
                  data-testid="terrenos-nome"
                  type="text"
                  value={formData.nome}
                  onChange={(e) => handleChange("nome", e.target.value)}
                  className={`input-custom ${errors.nome ? "form-error" : ""}`}
                  disabled={loading}
                />
                {errors.nome && (
                  <p className="text-destructive text-sm mt-1">{errors.nome}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  data-testid="terrenos-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className={`input-custom ${errors.email ? "form-error" : ""}`}
                  disabled={loading}
                />
                {errors.email && (
                  <p className="text-destructive text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Telefone */}
              <div>
                <Label htmlFor="telefone" className="text-sm font-medium mb-2 block">
                  Telefone
                </Label>
                <Input
                  id="telefone"
                  data-testid="terrenos-telefone"
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => handleChange("telefone", e.target.value)}
                  className="input-custom"
                  disabled={loading}
                />
              </div>

              {/* Localização */}
              <div>
                <Label htmlFor="localizacao" className="text-sm font-medium mb-2 block">
                  Localização *
                </Label>
                <Input
                  id="localizacao"
                  data-testid="terrenos-localizacao"
                  type="text"
                  placeholder="Ex: Matosinhos, Porto"
                  value={formData.localizacao}
                  onChange={(e) => handleChange("localizacao", e.target.value)}
                  className={`input-custom ${errors.localizacao ? "form-error" : ""}`}
                  disabled={loading}
                />
                {errors.localizacao && (
                  <p className="text-destructive text-sm mt-1">{errors.localizacao}</p>
                )}
              </div>

              {/* Área */}
              <div>
                <Label htmlFor="area" className="text-sm font-medium mb-2 block">
                  Área aproximada (m²)
                </Label>
                <Input
                  id="area"
                  data-testid="terrenos-area"
                  type="text"
                  placeholder="Ex: 500"
                  value={formData.area_m2}
                  onChange={(e) => handleChange("area_m2", e.target.value)}
                  className="input-custom"
                  disabled={loading}
                />
              </div>

              {/* Situação */}
              <div>
                <Label htmlFor="situacao" className="text-sm font-medium mb-2 block">
                  Situação *
                </Label>
                <Select
                  value={formData.situacao}
                  onValueChange={(value) => handleChange("situacao", value)}
                  disabled={loading}
                >
                  <SelectTrigger
                    data-testid="terrenos-situacao"
                    className={`input-custom ${errors.situacao ? "form-error" : ""}`}
                  >
                    <SelectValue placeholder="Seleccione a situação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urbano">Urbano</SelectItem>
                    <SelectItem value="em_estudo">Em estudo</SelectItem>
                    <SelectItem value="com_pip">Com PIP</SelectItem>
                    <SelectItem value="com_projecto">Com projecto</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
                {errors.situacao && (
                  <p className="text-destructive text-sm mt-1">{errors.situacao}</p>
                )}
              </div>

              {/* Mensagem */}
              <div>
                <Label htmlFor="mensagem" className="text-sm font-medium mb-2 block">
                  Mensagem ou link para documentação
                </Label>
                <Textarea
                  id="mensagem"
                  data-testid="terrenos-mensagem"
                  rows={4}
                  value={formData.mensagem}
                  onChange={(e) => handleChange("mensagem", e.target.value)}
                  className="input-custom resize-none"
                  disabled={loading}
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                data-testid="terrenos-submit"
                disabled={loading}
                className="btn-primary w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A enviar...
                  </>
                ) : (
                  "Pedir avaliação inicial"
                )}
              </Button>
            </form>
          </div>

          {/* Info Card */}
          <div className="hidden lg:flex items-center">
            <div className="card-custom bg-primary/5 border-primary/20">
              <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Processo discreto e eficiente
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Avaliação inicial sem compromisso</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Contacto directo com promotores qualificados</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Abordagem discreta e orientada a fecho</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Resposta em dias úteis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contacto Section
const ContactoSection = () => {
  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="section-spacing section-alt"
    >
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          <h2 data-testid="contacto-title" className="heading-section mb-8">
            Contacto
          </h2>

          <div className="space-y-4 mb-12">
            <a
              href="mailto:ivo@maisonporto.com"
              data-testid="contacto-email"
              className="inline-flex items-center gap-3 text-lg hover:text-primary transition-colors"
            >
              <Mail className="w-5 h-5" />
              ivo@maisonporto.com
            </a>

            <div className="pt-4">
              <a
                href="https://www.linkedin.com/in/ivopedrogomes/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contacto-linkedin"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                <Linkedin className="w-5 h-5" />
                LinkedIn
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <p data-testid="contacto-note" className="text-sm text-muted-foreground pt-4">
              Resposta em dias úteis.
            </p>
          </div>

          {/* Final CTA */}
          <div className="pt-8 border-t border-border">
            <p data-testid="contacto-cta-text" className="body-large mb-6">
              Pronto para receber oportunidades de investimento?
            </p>
            <Button
              data-testid="contacto-cta-btn"
              onClick={() => scrollToSection("newsletter")}
              className="btn-primary"
            >
              Subscrever newsletter
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="footer-primary py-12">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Brand */}
          <div className="text-center md:text-left">
            <p
              className="text-xl font-semibold mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ivo Gomes
            </p>
            <p className="text-sm text-primary-foreground/70">
              Investimento imobiliário no Porto/Norte
            </p>
          </div>

          {/* Links */}
          <div className="flex gap-6 text-sm">
            <a
              href="#"
              data-testid="footer-privacy"
              className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            >
              Política de privacidade
            </a>
            <a
              href="#"
              data-testid="footer-terms"
              className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            >
              Termos
            </a>
          </div>
        </div>

        {/* RGPD Note */}
        <div className="mt-8 pt-6 border-t border-primary-foreground/20 text-center">
          <p data-testid="footer-rgpd" className="text-xs text-primary-foreground/60">
            Ao subscrever, aceita receber a newsletter mensal. Pode cancelar a qualquer momento.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ParaQuemSection />
        <NewsletterSection />
        <SobreSection />
        <TerrenosSection />
        <ContactoSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
